/**
 * audioAlert.ts — Synthétiseur d'alertes sonores via la Web Audio API native.
 * 100% autonome, zéro fichier audio externe, fonctionne en mode PWA hors-ligne.
 */

let audioCtx: AudioContext | null = null;
const STORAGE_KEY_MUTED = 'namm_audio_muted';

/**
 * Récupère ou initialise le contexte audio partagé du navigateur.
 */
function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  if (!audioCtx) {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }

  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {
      // Ignorer si le geste utilisateur n'a pas encore eu lieu
    });
  }

  return audioCtx;
}

/**
 * Vérifie si le son est actuellement coupé.
 */
export function isAudioMuted(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY_MUTED) === 'true';
}

/**
 * Active ou coupe le son des alertes.
 */
export function setAudioMuted(muted: boolean): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY_MUTED, muted ? 'true' : 'false');
  if (!muted) {
    // Débloque immédiatement le contexte audio lors du clic utilisateur
    getAudioContext();
  }
}

/**
 * Joue une fréquence donnée avec enveloppe d'attaque et de relâchement.
 */
export function playTone(
  frequency: number,
  durationMs: number,
  options: {
    type?: OscillatorType;
    gain?: number;
    delayMs?: number;
  } = {}
): void {
  if (isAudioMuted()) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  const { type = 'sine', gain = 0.25, delayMs = 0 } = options;
  const startTime = ctx.currentTime + Math.max(0, delayMs / 1000);
  const stopTime = startTime + durationMs / 1000;

  const osc = ctx.createOscillator();
  const gainNode = ctx.createGain();

  osc.type = type;
  osc.frequency.setValueAtTime(frequency, startTime);

  // Enveloppe : attaque douce + extinction exponentielle
  gainNode.gain.setValueAtTime(0.0001, startTime);
  gainNode.gain.exponentialRampToValueAtTime(gain, startTime + 0.03);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, stopTime);

  osc.connect(gainNode);
  gainNode.connect(ctx.destination);

  osc.start(startTime);
  osc.stop(stopTime);
}

/**
 * Carillon futuriste à trois tons lors de l'expiration d'un compte à rebours (00:00).
 * Notes : Ré5 (587.33 Hz) -> La5 (880.00 Hz) -> Ré6 (1174.66 Hz).
 */
export function playCountdownFinishedSound(): void {
  if (isAudioMuted()) return;

  // Carillon ascendant mélodieux et clair
  playTone(587.33, 220, { type: 'triangle', gain: 0.35, delayMs: 0 });
  playTone(880.0, 260, { type: 'sine', gain: 0.3, delayMs: 140 });
  playTone(1174.66, 450, { type: 'sine', gain: 0.25, delayMs: 300 });
}

/**
 * Alerte discrète à double impulsion lorsqu'un jeu tourne sans chrono (>2 min).
 * Notes : 440 Hz (La4) -> 370 Hz (Fa#4).
 */
export function playUntrackedWarningSound(): void {
  if (isAudioMuted()) return;

  playTone(440.0, 160, { type: 'triangle', gain: 0.22, delayMs: 0 });
  playTone(369.99, 220, { type: 'sine', gain: 0.2, delayMs: 150 });
}

